import { observer } from "mobx-react-lite";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { useExonnHubContext } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubContext.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import {
  autoUpdate,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import * as styles from "./ViewAddedProductsButton.styles.ts";
import { OrderPreview } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/OrderPreview/OrderPreview.tsx";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useOne } from "src/system/useOne.tsx";
import type { B2bGateOrderResource } from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";

export const ViewAddedProductsButton = observer(() => {
  const store = useExonnHubStore();
  const isFullscreen = store.isFullscreen;
  const theme = useTheme();
  const isGreaterThanIpadPro = useMediaQuery(
    theme.deviceV2.greaterThan.ipadProLandscape,
  );
  const isGreaterThanLaptop = useMediaQuery(theme.deviceV2.greaterThan.laptop);

  const showText =
    (isFullscreen && isGreaterThanLaptop) ||
    (!isFullscreen && isGreaterThanIpadPro);
  const { orderId } = useExonnHubContext();
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);

  const { data } = useOne<B2bGateOrderResource>({
    id: orderId,
    resource: ResourceEnum.B2bGateOrder,
  });

  const { context, refs, floatingStyles } = useFloating({
    open: store.isOrderPreviewOpen,
    onOpenChange: (open) => store.setIsOrderPreviewOpen(open),
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: 5,
      }),
      shift({
        padding: 10,
      }),
      size({
        apply({ elements, availableHeight }) {
          const maxHeight = availableHeight - 10;
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`,
          });
        },
      }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      escapeKey: false,
    }),
  ]);

  const positions = (data?.data.b2b_gate_supplier_orders || []).flatMap(
    (order) => order.b2b_gate_supplier_order_positions || [],
  );
  return (
    <>
      <div css={styles.container}>
        {showText ? (
          <ButtonV2
            startAdornment={<i className={"icon-15-view_prod"} />}
            skin={"info"}
            variant={"outlined"}
            size={"sm"}
            count={data ? positions.length : undefined}
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            {t("openOrderPreview")}
          </ButtonV2>
        ) : (
          <IconButtonV2
            variant={"outlined"}
            size={"sm"}
            skin={"info"}
            count={data ? positions.length : undefined}
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            <span className={"icon-15-view_prod"} />
          </IconButtonV2>
        )}
      </div>

      {store.isOrderPreviewOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          css={styles.cartFloating}
          {...getFloatingProps()}
        >
          <OrderPreview />
        </div>
      )}
    </>
  );
});
